import React, {useCallback} from 'react';


import {RoleLink} from 'src/admin-rights-service';
import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getAdminRoleUpdateProgress, getAdminRoleUpdateResult} from 'src/openapi/admin/apis/BulkUpdateAdminRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UpdateAdminRoleItem} from 'src/openapi/admin/models/UpdateAdminRoleItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdateAdminRoleResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateAdminRoleItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateAdminRoleItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.userEmail}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>
                        <RoleLink
                            caption={res.oldRoleName}
                            profileId={res.oldRoleId}
                            blank={true}
                        />
                    </Cell>
                    <Cell>
                        <RoleLink
                            caption={res.newRoleName}
                            profileId={res.newRoleId}
                            blank={true}
                        />
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<UpdateAdminRoleItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId?.toString(),
                            oldRoleId: item.oldRoleName ? item.oldRoleId?.toString() : '',
                            newRoleId: item.newRoleName ? item.newRoleId?.toString() : '',
                            oldRoleName: item.oldRoleName || 'Deleted role',
                            newRoleName: item.newRoleName || 'Deleted role',
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell width={200}>Email</Cell>
                        <Cell width={200}>Old Role</Cell>
                        <Cell width={200}>New Role</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getAdminRoleUpdateProgress({id}))}
            getResult={() => apiDispatch(getAdminRoleUpdateResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
